import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { novaListSessions, novaDeleteSession, novaRenameSession, novaGetSession, novaDownloadReport } from '../services/novaService';

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ChatHistory() {
  const { instance: msalInstance } = useMsal();
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedReports, setExpandedReports] = useState({});
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await novaListSessions(msalInstance);
      const withReports = await Promise.all(list.map(async (s) => {
        try {
          const full = await novaGetSession(msalInstance, s.sessionId);
          return { ...s, reports: full.reports || [] };
        } catch {
          return { ...s, reports: [] };
        }
      }));
      setSessions(withReports);
    } catch {
      setSessions([]);
    }
    setLoading(false);
  }, [msalInstance]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (sid) => {
    try {
      await novaDeleteSession(msalInstance, sid);
      setSessions(prev => (prev || []).filter(s => s.sessionId !== sid));
    } catch {}
  };

  const handleRename = async (sid) => {
    if (!renameVal.trim()) return;
    try {
      await novaRenameSession(msalInstance, sid, renameVal.trim());
      setSessions(prev => (prev || []).map(s => s.sessionId === sid ? { ...s, title: renameVal.trim() } : s));
      setRenamingId(null);
    } catch {}
  };

  const handleContinue = (sid) => {
    sessionStorage.setItem('novaResumeSession', sid);
    window.dispatchEvent(new CustomEvent('novaOpenPanel'));
  };

  const handleReportDownload = async (report) => {
    try {
      const blob = await novaDownloadReport(msalInstance, report.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.filename || 'report';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div className="content">
      <div className="card">
        <div className="ch">
          <div className="ch-ico">💬</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 22, color: 'var(--navy)' }}>Nova Chat History</div>
          </div>
        </div>
        <div style={{ padding: '0 16px 16px' }}>
          {loading ? (
            <div style={{ color: '#999', fontSize: 13, padding: 24, textAlign: 'center' }}>Loading...</div>
          ) : (sessions || []).length === 0 ? (
            <div style={{ color: '#999', fontSize: 13, padding: 24, textAlign: 'center' }}>No saved conversations yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--navy)', fontSize: 13, fontWeight: 700, borderBottom: '2px solid #eef3f7' }}>Conversation</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--navy)', fontSize: 13, fontWeight: 700, borderBottom: '2px solid #eef3f7', width: 160 }}>Last Updated</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--navy)', fontSize: 13, fontWeight: 700, borderBottom: '2px solid #eef3f7', width: 200 }}>Reports</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--navy)', fontSize: 13, fontWeight: 700, borderBottom: '2px solid #eef3f7', width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(sessions || []).map(s => (
                    <tr key={s.sessionId} style={{ borderBottom: '1px solid #eef3f7' }}
                      onMouseEnter={e => { for (const td of e.currentTarget.children) td.style.background = '#f8fafc'; }}
                      onMouseLeave={e => { for (const td of e.currentTarget.children) td.style.background = ''; }}>
                      <td style={{ padding: '10px 12px', fontSize: 13 }}>
                        {renamingId === s.sessionId ? (
                          <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input
                              value={renameVal}
                              onChange={e => setRenameVal(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleRename(s.sessionId); if (e.key === 'Escape') setRenamingId(null); }}
                              autoFocus
                              style={{ fontSize: 13, border: '1px solid var(--teal)', borderRadius: 4, padding: '2px 6px', width: 200 }}
                            />
                            <button onClick={() => handleRename(s.sessionId)} style={{ background: 'var(--teal)', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>Save</button>
                            <button onClick={() => setRenamingId(null)} style={{ background: 'none', border: '1px solid #ccc', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                          </span>
                        ) : (
                          <span style={{ cursor: 'pointer' }} onClick={() => { setRenamingId(s.sessionId); setRenameVal(s.title || ''); }} title="Click to rename">
                            {s.title || 'New conversation'}
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" style={{ marginLeft: 6, verticalAlign: 'middle' }}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: '#888' }}>{fmtDate(s.updatedAt)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {(s.reports || []).length === 0 ? (
                          <span style={{ fontSize: 11, color: '#bbb' }}>—</span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {(expandedReports[s.sessionId] ? s.reports : s.reports.slice(0, 2)).map((r, ri) => (
                              <button key={ri} onClick={() => handleReportDownload(r)} style={{ fontSize: 11, color: 'var(--teal)', background: 'none', border: '1px solid var(--teal)', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', whiteSpace: 'nowrap' }} title={r.filename}>
                                {r.reportType || 'Report'} {r.filename ? r.filename.split('.').pop().toUpperCase() : ''}
                              </button>
                            ))}
                            {s.reports.length > 2 && !expandedReports[s.sessionId] && (
                              <button onClick={() => setExpandedReports(p => ({ ...p, [s.sessionId]: true }))} style={{ fontSize: 10, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}>+{s.reports.length - 2} more</button>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button onClick={() => handleContinue(s.sessionId)} style={{ fontSize: 11, color: '#fff', background: 'var(--teal)', border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer' }}>Continue</button>
                          <button onClick={() => handleDelete(s.sessionId)} style={{ fontSize: 11, color: '#E53935', background: 'none', border: '1px solid #E53935', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
